import { waitWords } from "@/lib/cadence-plan";
import { MERGE_FIELDS, fieldsIn, hasBrokenField } from "@/lib/text-templates";
import type { FormFieldConfig, FormFieldType } from "@/lib/types";

/**
 * Workflows (ADR-111 in platform, ACL-367): an admin's "when this happens, wait, then text the
 * lead", kept as a draft and published as versions. core-api's `/workflows` routes keep them and
 * `src/workflow_body.py` checks the body; these are its shapes and the console's pure rules over
 * them.
 *
 * An If/Else (ACL-369) holds named paths, as GoHighLevel's does: `branches`, read top to bottom, the
 * lead taking the first whose conditions all hold, and `none` for everyone else. Each path is a list
 * of steps, so the steps are a tree. The paths never join again: each runs to its own END, so an
 * If/Else is always the last step of its list. A step's id is unique in the whole tree, a path's id
 * in its If/Else, and every rule here that finds, changes or removes a step looks in the paths too.
 *
 * Words an admin reads never say "run", "event" or "slug". A lead is "in" a workflow, a workflow
 * "starts when" something happens, and a text is chosen by its template's name.
 */

export type WorkflowStatus = "draft" | "published" | "archived";
export type WorkflowEvent = "lead.created" | "disposition.set" | "lead.stage_changed" | "message.received" | "call.ended";
export type WorkflowOrigin = "intake" | "trigger" | "console" | "import" | "hand_dial";
export type WorkflowStepType =
  | "wait"
  | "send_text"
  | "send_email"
  | "if"
  | "set_stage"
  | "set_field"
  | "note"
  | "assign_owner"
  | "create_task"
  | "send_slack";
/** Which way a call went (ACL-408): one we made, or one the lead made. */
export type CallDirection = "outbound" | "inbound";
/** Whether a rep talked on the call: either way, yes, or no. */
export type CallAnswered = "any" | "yes" | "no";
export type ConditionField = "firm" | "language" | "state" | "stage" | "source";
export type ConditionOp = "is" | "is_not";

export interface WorkflowStartsWhen {
  event: WorkflowEvent | null;
  origins: WorkflowOrigin[];
  dispositions: string[];
  /** The stage changes (ACL-408): from one of these (none: from any stage) to one of these. Absent
   *  from a body an older core-api kept. */
  from_stages?: string[];
  to_stages?: string[];
  /** A call ends (ACL-408): which way it went, whether a rep talked on it, for how long at least. */
  call_directions?: CallDirection[];
  call_answered?: CallAnswered;
  min_talk_seconds?: number;
}

export interface WorkflowSettings {
  stop_on_reply: boolean;
  max_late_minutes: number;
}

export interface WaitStep {
  id: string;
  type: "wait";
  seconds: number;
}

export interface SendTextStep {
  id: string;
  type: "send_text";
  template: string | null;
}

/** Send email (ACL-378, ADR-198 in platform): one of the tenant's email templates, with the STOP
 *  line under it. Waits between them make a cadence. */
export interface SendEmailStep {
  id: string;
  type: "send_email";
  template: string | null;
}

/** Update stage (ACL-370): one of the tenant's working or scheduled stages. A scheduled stage also
 *  says when the lead is called, this long after the step runs. */
export interface SetStageStep {
  id: string;
  type: "set_stage";
  stage: string | null;
  call_in_seconds: number | null;
}

/** What Update field stores: text (a number as the text typed), yes or no, or option keys. */
export type FieldStepValue = string | number | boolean | string[];

/** Update field (ACL-370): one of the lead's own fields, given a value. */
export interface SetFieldStep {
  id: string;
  type: "set_field";
  field: string | null;
  value: FieldStepValue | null;
}

/** Add note (ACL-370): a note on the lead's timeline, from the workflow. */
export interface NoteStep {
  id: string;
  type: "note";
  text: string | null;
}

/** Assign owner (ACL-408): gives the lead to one of the tenant's people. */
export interface AssignOwnerStep {
  id: string;
  type: "assign_owner";
  user_id: number | null;
}

/** Create task (ACL-408): a task about the lead, due so long after the step, for one person or for
 *  whoever owns the lead then. */
export interface CreateTaskStep {
  id: string;
  type: "create_task";
  title: string | null;
  due_in_seconds: number | null;
  assignee: "user" | "owner" | null;
  user_id: number | null;
}

/** Send Slack message (ACL-408): words with the lead's details, to a channel of one of the tenant's
 *  Slack connections. The channel's name is kept to show it without asking Slack again. */
export interface SendSlackStep {
  id: string;
  type: "send_slack";
  connection_id: number | null;
  channel_id: string | null;
  channel_name: string | null;
  text: string | null;
}

/** As `workflow_body` in core-api: a scheduled stage's call is 1 minute to 180 days away. */
export const MIN_CALL_IN_SECONDS = 60;
export const MAX_CALL_IN_SECONDS = 180 * 86400;
export const MAX_NOTE_CHARS = 2000;
/** As `workflow_body` in core-api (ACL-408). */
export const MAX_TASK_TITLE = 200;
export const MAX_DUE_IN_SECONDS = 90 * 86400;
export const MAX_SLACK_CHARS = 3000;
export const MAX_MIN_TALK_SECONDS = 4 * 3600;

/** One row of an If/Else: `is` any of the values, or `is_not` any of them. */
export interface Condition {
  field: ConditionField;
  op: ConditionOp;
  values: string[];
}

/** One named path of an If/Else: taken when every condition holds and no path above it matched. */
export interface WorkflowBranch {
  /** Stays the same when it is renamed or moved: what a run records. */
  id: string;
  name: string;
  conditions: Condition[];
  steps: WorkflowStep[];
}

export interface IfStep {
  id: string;
  type: "if";
  /** Read top to bottom; the first whose conditions all hold is taken. */
  branches: WorkflowBranch[];
  /** Where a lead goes when no path matched. Always last; it may be renamed. */
  none: { name: string; steps: WorkflowStep[] };
}

export type WorkflowStep =
  | WaitStep
  | SendTextStep
  | SendEmailStep
  | IfStep
  | SetStageStep
  | SetFieldStep
  | NoteStep
  | AssignOwnerStep
  | CreateTaskStep
  | SendSlackStep;
/** The steps that change the lead (ACL-370). */
export type ActionStep = SetStageStep | SetFieldStep | NoteStep;
/** A path of an If/Else: a branch's id, or `NONE_PATH`. */
export type PathKey = string;
export const NONE_PATH: PathKey = "none";
/** Which list of steps: the workflow's own (null), or one path of an If/Else. */
export type StepList = { ifId: string; path: PathKey } | null;

export interface WorkflowBody {
  starts_when: WorkflowStartsWhen;
  settings: WorkflowSettings;
  steps: WorkflowStep[];
}

export interface WorkflowCounts {
  enrolled: number;
  active: number;
  completed: number;
  exited: number;
  failed: number;
}

export interface WorkflowSummary {
  id: string;
  tenant_id: number;
  name: string;
  folder: string | null;
  status: WorkflowStatus;
  starts_when: { event: WorkflowEvent; label: string } | null;
  published_version_n: number | null;
  has_unpublished_changes: boolean;
  counts: WorkflowCounts;
  updated_at: string;
  updated_by_name: string | null;
}

export interface WorkflowVersion {
  version_n: number;
  published_at: string;
  published_by_name: string | null;
}

/** One published version with its body (`GET /workflows/{id}/versions/{n}`, ACL-406). */
export interface WorkflowVersionDetail extends WorkflowVersion {
  body: WorkflowBody;
}

export interface WorkflowDetail extends WorkflowSummary {
  draft_body: WorkflowBody;
  published_body: WorkflowBody | null;
  versions: WorkflowVersion[];
}

export type WorkflowRunStatus = "active" | "completed" | "exited" | "failed";

export interface WorkflowRunStep {
  step_id: string;
  type: string;
  /** `waiting`: a text the hours rule or the day's limit moved to later (ACL-385). */
  status: "waiting" | "sending" | "done" | "failed";
  at: string;
  error: string | null;
  message_id: string | null;
  /** The path an If/Else sent the lead down (its id, `none` for None) and its name then. */
  branch?: string | null;
  branch_name?: string | null;
  /** Why a text is waiting: `quiet_hours` or `daily_limit`. */
  held?: string | null;
  /** What an action did, named as it was then (ACL-370): the stage it moved the lead to, the field
   *  it set (never the value). */
  stage_name?: string | null;
  field_name?: string | null;
  /** ACL-408: whom Assign owner gave the lead to or Create task made the task for, the task's
   *  title and due time, and the Slack channel a message went to (or was to go to). */
  person_name?: string | null;
  task_id?: string | null;
  task_title?: string | null;
  task_due?: string | null;
  channel_name?: string | null;
}

export interface WorkflowRun {
  id: string;
  lead_id: string;
  lead_name: string | null;
  /** Deleted from the console: the name is not shown, and there is no record to open. */
  lead_deleted: boolean;
  status: WorkflowRunStatus;
  started_at: string;
  ended_at: string | null;
  end_reason: string | null;
  next_run_at: string | null;
  /** The step the lead takes next and its kind, from the version the lead is on. */
  next_step: string | null;
  next_step_type: WorkflowStepType | null;
  version_n: number | null;
  steps: WorkflowRunStep[];
}

/** One of a start's choices, and whether a workflow that picks it can be published today. */
export interface CatalogChoice<T extends string = string> {
  value: T;
  label: string;
  available?: boolean;
  note?: string;
}

export interface CatalogStart {
  event: WorkflowEvent;
  label: string;
  available: boolean;
  note?: string;
  /** What the panel says under the start, when it has something to say. */
  description?: string;
  /** The tenant's outcomes; a `retired` one is listed only so a draft naming it reads in words. */
  dispositions?: { slug: string; label: string; retired?: boolean }[];
  origins?: { value: WorkflowOrigin; label: string }[];
  default_origins?: WorkflowOrigin[];
  /** Every stage of the tenant, end stages too, for "The stage changes" (ACL-408). */
  stages?: { slug: string; label: string; group: "working" | "scheduled" | "exit" }[];
  /** For "A call ends" (ACL-408). */
  call_directions?: CatalogChoice<CallDirection>[];
  call_answered?: CatalogChoice<CallAnswered>[];
}

/** One of the tenant's active people (ACL-408): whom a lead may be given and a task assigned. */
export interface CatalogPerson {
  id: number;
  name: string;
}

/** One of the tenant's Slack connections (ACL-408). Not `usable`: turned off, or Slack refused its
 *  token; a draft may name it, a published workflow may not. */
export interface CatalogSlack {
  id: number;
  name: string;
  usable: boolean;
  team_name?: string | null;
}

export interface CatalogTemplate {
  slug: string;
  name: string;
  languages: string[];
  /** Off: a draft that names it still reads in words, and cannot be published until it is on.
   *  Absent from an older core-api, which listed only the ones that were on. */
  active?: boolean;
  /** A text step's (`sms`) or an email step's (ACL-378). Absent from an older core-api: a text's. */
  channel?: "sms" | "email";
}

/** A lead detail a text may name (ACL-385): with the value the preview fills, and the most
 *  characters a lead's value is given when a text is checked for length. */
export interface CatalogMergeField {
  key: string;
  label: string;
  sample: string;
  longest?: number;
}

/** A field an If/Else can test: its values to pick, or typed (a source, matched by how it starts). */
export interface CatalogConditionField {
  field: ConditionField;
  label: string;
  kind: "pick" | "text";
  values: { value: string; label: string }[];
  note?: string;
}

/** A stage Update stage may move a lead to (ACL-370): working or scheduled, never an end stage. */
export interface CatalogStage {
  slug: string;
  label: string;
  group: "working" | "scheduled";
}

/** A field of the lead Update field may set (ACL-370), with its settings, so the builder draws the
 *  input a rep's edit has. */
export interface CatalogField {
  key: string;
  title: string;
  type: FormFieldType;
  config: FormFieldConfig;
}

export interface WorkflowCatalog {
  starts: CatalogStart[];
  /** `available: false` is a step that can be put in a draft and not published yet, with a
   *  `note` saying why (a text, until templates exist). */
  step_types: { type: WorkflowStepType; label: string; available?: boolean; note?: string }[];
  templates: CatalogTemplate[];
  merge_fields: CatalogMergeField[];
  /** What an If/Else can test, with the tenant's firms and stages. */
  condition_fields?: CatalogConditionField[];
  /** What the actions may name (ACL-370). Absent from an older core-api. */
  stages?: CatalogStage[];
  fields?: CatalogField[];
  /** ACL-408. Absent from an older core-api. */
  people?: CatalogPerson[];
  slack_connections?: CatalogSlack[];
}

export interface WorkflowInput {
  name: string;
  folder: string | null;
  draft_body: WorkflowBody;
  /** The `updated_at` the editor read: a save over somebody else's newer one is refused (409). */
  updated_at?: string;
}

export const DEFAULT_ORIGINS: WorkflowOrigin[] = ["intake", "trigger"];

/** core-api's bounds (`workflow_body.py`): a wait of a minute to thirty days, fifty steps. */
export const MIN_WAIT_SECONDS = 60;
export const MAX_WAIT_SECONDS = 30 * 24 * 3600;
export const MAX_STEPS = 50;
/** An If/Else inside an If/Else inside an If/Else, and no deeper. */
export const MAX_DEPTH = 3;
export const MAX_CONDITIONS = 10;
/** Named paths an If/Else may hold, besides None. */
export const MAX_BRANCHES = 10;
export const MAX_BRANCH_NAME = 40;
export const MAX_VALUES = 200;
/** The firm value for a lead no firm resolves for. No firm's code starts with `_`. */
export const NO_FIRM = "_none";
export const CONDITION_FIELDS: Record<ConditionField, string> = {
  firm: "Firm",
  language: "Language",
  state: "State",
  stage: "Stage",
  source: "Source",
};
/** A new wait step's first value. */
export const DEFAULT_WAIT_SECONDS = 3600;
/** How late a text may be before it is skipped, in minutes. */
export const LATE_CHOICES = [15, 30, 60, 120, 6 * 60, 24 * 60] as const;

/** Whether a step of this kind can be published today. Unknown means yes: the server checks. */
export function stepAvailable(catalog: WorkflowCatalog | undefined, type: WorkflowStepType): boolean {
  return catalog?.step_types.find((t) => t.type === type)?.available !== false;
}

/**
 * The catalog's reason a choice cannot be picked yet, under a "Coming soon" tag: the tag already
 * says "Coming soon", so the server's own lead-in is left off ("Text templates are not here yet.").
 */
export function soonNote(note: string | undefined): string {
  const rest = (note ?? "").replace(/^coming soon[:.]?\s*/i, "").trim();
  return rest ? rest.charAt(0).toUpperCase() + rest.slice(1) : "Not here yet.";
}

export function emptyBody(): WorkflowBody {
  return {
    starts_when: {
      event: null,
      origins: [...DEFAULT_ORIGINS],
      dispositions: [],
      from_stages: [],
      to_stages: [],
      call_directions: ["outbound"],
      call_answered: "any",
      min_talk_seconds: 0,
    },
    settings: { stop_on_reply: true, max_late_minutes: 60 },
    steps: [],
  };
}

/** A wait in words: "1 minute", "4 hours", "2 days". */
export function waitText(seconds: number): string {
  const words = waitWords(Math.round(seconds / 60));
  return words === "1 min" ? "1 minute" : words.replace(/ min$/, " minutes");
}

// --- the steps as a tree ---------------------------------------------------------------------

/** An If/Else's paths in order, None last, each with the key a `StepList` names it by. */
export function pathsOf(step: IfStep): { key: PathKey; name: string; steps: WorkflowStep[] }[] {
  return [
    ...step.branches.map((b) => ({ key: b.id, name: b.name, steps: b.steps })),
    { key: NONE_PATH, name: step.none.name, steps: step.none.steps },
  ];
}

function stepsOfPath(step: IfStep, key: PathKey): WorkflowStep[] {
  if (key === NONE_PATH) return step.none.steps;
  return step.branches.find((b) => b.id === key)?.steps ?? [];
}

function withPathSteps(step: IfStep, key: PathKey, steps: WorkflowStep[]): IfStep {
  if (key === NONE_PATH) return { ...step, none: { ...step.none, steps } };
  return { ...step, branches: step.branches.map((b) => (b.id === key ? { ...b, steps } : b)) };
}

/** Every step, in the order an admin reads them: a step, then each of its paths, None last. */
export function allSteps(steps: readonly WorkflowStep[]): WorkflowStep[] {
  return steps.flatMap((s) => (s.type === "if" ? [s, ...pathsOf(s).flatMap((p) => allSteps(p.steps))] : [s]));
}

/** How many steps the workflow has, those inside paths included: core-api's limit counts them all. */
export function stepCount(body: WorkflowBody): number {
  return allSteps(body.steps).length;
}

export function findStep(body: WorkflowBody, id: string): WorkflowStep | undefined {
  return allSteps(body.steps).find((s) => s.id === id);
}

/** Every step through `fn`, paths included: a step it returns null for is taken out. */
function mapSteps(steps: readonly WorkflowStep[], fn: (step: WorkflowStep) => WorkflowStep | null): WorkflowStep[] {
  return steps.flatMap((s): WorkflowStep[] => {
    const next = fn(s);
    if (next === null) return [];
    if (next.type !== "if") return [next];
    return [
      {
        ...next,
        branches: next.branches.map((b) => ({ ...b, steps: mapSteps(b.steps, fn) })),
        none: { ...next.none, steps: mapSteps(next.none.steps, fn) },
      },
    ];
  });
}

/** The body with one If/Else changed, wherever it is. */
function inIf(body: WorkflowBody, ifId: string, fn: (step: IfStep) => IfStep): WorkflowBody {
  return { ...body, steps: mapSteps(body.steps, (s) => (s.type === "if" && s.id === ifId ? fn(s) : s)) };
}

/** The body with one list of steps changed, wherever it is. */
function inList(body: WorkflowBody, list: StepList, fn: (steps: WorkflowStep[]) => WorkflowStep[]): WorkflowBody {
  if (list === null) return { ...body, steps: fn(body.steps) };
  return inIf(body, list.ifId, (s) => withPathSteps(s, list.path, fn(stepsOfPath(s, list.path))));
}

/** The steps of a list, wherever it is. */
export function stepsOf(body: WorkflowBody, list: StepList): WorkflowStep[] {
  if (list === null) return body.steps;
  const step = findStep(body, list.ifId);
  return step?.type === "if" ? stepsOfPath(step, list.path) : [];
}

/** The list a step is in, and where. */
export function placeOf(body: WorkflowBody, id: string): { list: StepList; steps: WorkflowStep[]; index: number } | null {
  const look = (steps: WorkflowStep[], list: StepList): ReturnType<typeof placeOf> => {
    const index = steps.findIndex((s) => s.id === id);
    if (index >= 0) return { list, steps, index };
    for (const s of steps) {
      if (s.type !== "if") continue;
      for (const path of pathsOf(s)) {
        const found = look(path.steps, { ifId: s.id, path: path.key });
        if (found) return found;
      }
    }
    return null;
  };
  return look(body.steps, null);
}

/** How many If/Else steps a list sits inside: 0 for the workflow's own. */
export function depthOf(body: WorkflowBody, list: StepList): number {
  if (list === null) return 0;
  const place = placeOf(body, list.ifId);
  return place ? depthOf(body, place.list) + 1 : 0;
}

/** How many If/Else levels a list holds: 0 with none, 1 with one, 2 with one inside another. */
function ifLevels(steps: readonly WorkflowStep[]): number {
  return Math.max(
    0,
    ...steps.map((s) => (s.type === "if" ? 1 + Math.max(...pathsOf(s).map((p) => ifLevels(p.steps))) : 0)),
  );
}

/**
 * Whether an If/Else can go at `index` of a list: it sits at most three deep, and the steps under
 * it, which move into its first path, stay within that too.
 */
export function canAddIf(body: WorkflowBody, list: StepList, index: number): boolean {
  if (stepCount(body) >= MAX_STEPS) return false;
  return depthOf(body, list) + 1 + ifLevels(stepsOf(body, list).slice(index)) <= MAX_DEPTH;
}

const ID_PREFIX: Record<WorkflowStepType, string> = {
  wait: "wait",
  send_text: "text",
  send_email: "email",
  if: "if",
  set_stage: "stage",
  set_field: "field",
  note: "note",
  assign_owner: "owner",
  create_task: "task",
  send_slack: "slack",
};

/** A new step's id: `wait-2`, `text-1`, the first number not taken anywhere in the workflow. Ids
 *  stay put across edits, so a published workflow's steps keep their names when one is added
 *  before them. */
export function newStepId(steps: readonly WorkflowStep[], type: WorkflowStepType): string {
  const taken = new Set(allSteps(steps).map((s) => s.id));
  for (let n = 1; ; n++) {
    const id = `${ID_PREFIX[type]}-${n}`;
    if (!taken.has(id)) return id;
  }
}

/** A new path: the first `branch-N` id and "Branch N" name its If/Else does not have yet, with one
 *  condition on the firm, since that is what GoHighLevel's paths are most often about. */
export function newBranch(step: Pick<IfStep, "branches" | "none">): WorkflowBranch {
  const ids = new Set(step.branches.map((b) => b.id));
  const names = new Set([...step.branches.map((b) => b.name), step.none.name].map((n) => n.trim().toLowerCase()));
  let n = 1;
  while (ids.has(`branch-${n}`) || names.has(`branch ${n}`)) n++;
  return { id: `branch-${n}`, name: `Branch ${n}`, conditions: [{ field: "firm", op: "is", values: [] }], steps: [] };
}

/** A new If/Else starts with one path and None, and the panel opens on the path's condition. */
export function newStep(steps: readonly WorkflowStep[], type: WorkflowStepType): WorkflowStep {
  const id = newStepId(steps, type);
  if (type === "wait") return { id, type, seconds: DEFAULT_WAIT_SECONDS };
  if (type === "send_text") return { id, type, template: null };
  if (type === "send_email") return { id, type, template: null };
  if (type === "set_stage") return { id, type, stage: null, call_in_seconds: null };
  if (type === "set_field") return { id, type, field: null, value: null };
  if (type === "note") return { id, type, text: null };
  if (type === "assign_owner") return { id, type, user_id: null };
  if (type === "create_task") return { id, type, title: null, due_in_seconds: null, assignee: null, user_id: null };
  if (type === "send_slack") return { id, type, connection_id: null, channel_id: null, channel_name: null, text: null };
  const none = { name: "None", steps: [] };
  return { id, type, branches: [newBranch({ branches: [], none })], none };
}

/**
 * The body with a new step of this kind at `index` of a list (the workflow's own when `list` is
 * left out, its end when `index` is). Nothing follows an If/Else, so a new If/Else takes the steps
 * that were under it into its first path, and any other step lands above the If/Else that ends its
 * list.
 */
export function withStep(body: WorkflowBody, type: WorkflowStepType, index?: number, list: StepList = null): WorkflowBody {
  const step = newStep(body.steps, type);
  return inList(body, list, (steps) => {
    const last = steps.length - 1;
    const end = steps[last]?.type === "if" ? last : steps.length;
    // Never past an If/Else that ends the list, whatever the "+" asked for: its list may have changed
    // since its panel opened (an undo, another admin's save), and nothing may follow an If/Else.
    const at = Math.max(0, Math.min(index ?? end, end));
    if (step.type === "if") {
      const [first, ...rest] = step.branches;
      return [...steps.slice(0, at), { ...step, branches: [{ ...first!, steps: steps.slice(at) }, ...rest] }];
    }
    return [...steps.slice(0, at), step, ...steps.slice(at)];
  });
}

/** The body without a step, wherever it is. An If/Else goes with all its paths. */
export function withoutStep(body: WorkflowBody, id: string): WorkflowBody {
  return { ...body, steps: mapSteps(body.steps, (s) => (s.id === id ? null : s)) };
}

/** Whether a step can move one place in its own list: never past an end, and never an If/Else or
 *  past one, which stays last in its list. */
export function canMove(body: WorkflowBody, id: string, by: -1 | 1): boolean {
  const place = placeOf(body, id);
  if (!place) return false;
  const other = place.steps[place.index + by];
  return other !== undefined && other.type !== "if" && place.steps[place.index]!.type !== "if";
}

/** The body with a step moved one place up (-1) or down (+1) in its own list; unchanged when it
 *  cannot move (`canMove`). */
export function movedStep(body: WorkflowBody, id: string, by: -1 | 1): WorkflowBody {
  const place = placeOf(body, id);
  if (!place || !canMove(body, id, by)) return body;
  return inList(body, place.list, (steps) => {
    const next = [...steps];
    [next[place.index], next[place.index + by]] = [next[place.index + by]!, next[place.index]!];
    return next;
  });
}

export function withStepChange(
  body: WorkflowBody,
  id: string,
  change:
    | Partial<WaitStep>
    | Partial<SendTextStep>
    | Partial<SendEmailStep>
    | Partial<SetStageStep>
    | Partial<SetFieldStep>
    | Partial<NoteStep>
    | Partial<AssignOwnerStep>
    | Partial<CreateTaskStep>
    | Partial<SendSlackStep>,
): WorkflowBody {
  return {
    ...body,
    steps: mapSteps(body.steps, (s) => (s.id === id ? ({ ...s, ...change, id: s.id, type: s.type } as WorkflowStep) : s)),
  };
}

// --- an If/Else's paths ---------------------------------------------------------------------------

/** The If/Else with one more path, above None; unchanged at the limit. */
export function withBranch(body: WorkflowBody, ifId: string): WorkflowBody {
  return inIf(body, ifId, (s) =>
    s.branches.length >= MAX_BRANCHES ? s : { ...s, branches: [...s.branches, newBranch(s)] },
  );
}

/** The If/Else without one of its paths, and the steps on it. */
export function withoutBranch(body: WorkflowBody, ifId: string, branchId: string): WorkflowBody {
  return inIf(body, ifId, (s) => ({ ...s, branches: s.branches.filter((b) => b.id !== branchId) }));
}

/** A path moved one place up or down among the named paths; None stays last. */
export function movedBranch(body: WorkflowBody, ifId: string, branchId: string, by: -1 | 1): WorkflowBody {
  return inIf(body, ifId, (s) => {
    const from = s.branches.findIndex((b) => b.id === branchId);
    const to = from + by;
    if (from < 0 || to < 0 || to >= s.branches.length) return s;
    const branches = [...s.branches];
    [branches[from], branches[to]] = [branches[to]!, branches[from]!];
    return { ...s, branches };
  });
}

/** A path's name or conditions changed; its id and steps stay. */
export function withBranchChange(
  body: WorkflowBody,
  ifId: string,
  branchId: string,
  change: Partial<Pick<WorkflowBranch, "name" | "conditions">>,
): WorkflowBody {
  return inIf(body, ifId, (s) => ({
    ...s,
    branches: s.branches.map((b) => (b.id === branchId ? { ...b, ...change, id: b.id, steps: b.steps } : b)),
  }));
}

export function withNoneName(body: WorkflowBody, ifId: string, name: string): WorkflowBody {
  return inIf(body, ifId, (s) => ({ ...s, none: { ...s.none, name } }));
}

/** The body starting on another event. The outcomes and origins already picked are kept, so
 *  switching back and forth loses nothing. */
export function withStart(body: WorkflowBody, event: WorkflowEvent | null): WorkflowBody {
  return { ...body, starts_when: { ...body.starts_when, event } };
}

/** Add an item to a list, or take it out. */
export function toggled<T>(list: readonly T[], item: T): T[] {
  return list.includes(item) ? list.filter((x) => x !== item) : [...list, item];
}

export function workflowPayload(name: string, folder: string, body: WorkflowBody, updatedAt?: string): WorkflowInput {
  return { name: name.trim(), folder: folder.trim() || null, draft_body: body, ...(updatedAt ? { updated_at: updatedAt } : {}) };
}

/** The tenant's outcome labels by slug, retired ones too, from the catalog. */
export function outcomeLabels(catalog: WorkflowCatalog | undefined): Map<string, string> {
  const offered = catalog?.starts.find((s) => s.event === "disposition.set");
  return new Map((offered?.dispositions ?? []).map((d) => [d.slug, d.label]));
}

/** One thing that stops a draft from being published, as core-api would say it. */
export interface WorkflowProblem {
  message: string;
  stepId: string | null;
}

/**
 * What core-api's publish would refuse (`workflow_body.publish_problem`), in its words, so the
 * page can say it on the card before anything is sent. The server is the authority and checks
 * again; this only keeps a click from being the way to find out.
 */
export function publishProblem(body: WorkflowBody, catalog: WorkflowCatalog | undefined): WorkflowProblem | null {
  const start = body.starts_when;
  if (start.event === null) return { message: "Choose when the workflow starts.", stepId: null };
  const offered = catalog?.starts.find((s) => s.event === start.event);
  if (start.event === "lead.created") {
    if (offered && !offered.available) {
      return {
        message: "A workflow that starts when a new lead arrives can't be published yet: new leads are not announced to workflows yet.",
        stepId: null,
      };
    }
    if (start.origins.length === 0) return { message: "Choose where the new leads come from.", stepId: null };
  }
  if (start.event === "disposition.set") {
    if (start.dispositions.length === 0) {
      return { message: "Choose at least one outcome the workflow starts on.", stepId: null };
    }
    const known = new Set((offered?.dispositions ?? []).filter((d) => !d.retired).map((d) => d.slug));
    const unknown = offered ? start.dispositions.find((slug) => !known.has(slug)) : undefined;
    if (unknown) {
      const label = outcomeLabels(catalog).get(unknown) ?? unknown;
      return { message: `"${label}" is no longer one of your outcomes. Remove it to publish.`, stepId: null };
    }
  }
  const startProblem = moreStartProblem(start, offered);
  if (startProblem) return { message: startProblem, stepId: null };
  if (body.steps.length === 0) return { message: "Add at least one step.", stepId: null };
  for (const step of allSteps(body.steps)) {
    if (step.type === "if") {
      const problem = conditionProblem(step, catalog);
      if (problem) return problem;
    }
    if (step.type === "set_stage" || step.type === "set_field" || step.type === "note") {
      const problem = actionProblem(step, catalog);
      if (problem) return problem;
      continue;
    }
    if (step.type === "assign_owner" || step.type === "create_task" || step.type === "send_slack") {
      const problem = moreActionProblem(step, catalog);
      if (problem) return problem;
      continue;
    }
    if (step.type === "send_email") {
      if (!stepAvailable(catalog, "send_email")) {
        return { message: `Step "${step.id}": emails can't be sent from a workflow yet.`, stepId: step.id };
      }
      if (!step.template) return { message: `Step "${step.id}": choose an email template.`, stepId: step.id };
      const problem = emailTemplateProblemOf(step.id, step.template, catalog);
      if (problem) return problem;
      continue;
    }
    if (step.type !== "send_text") continue;
    if (!stepAvailable(catalog, "send_text")) {
      return { message: `Step "${step.id}": texts can't be sent yet: text templates are coming soon.`, stepId: step.id };
    }
    if (!step.template) return { message: `Step "${step.id}": choose a template.`, stepId: step.id };
    const problem = templateProblem(step.id, step.template, catalog);
    if (problem) return problem;
  }
  // A lead goes through a workflow once, so one where no step acts on the lead would use them up
  // for nothing: waits and If/Elses only decide when and which way. Asked last, so a problem the
  // admin can fix now is named first.
  const last = body.steps[body.steps.length - 1];
  if (last && allSteps(body.steps).every((step) => step.type === "wait" || step.type === "if")) {
    return {
      message:
        last.type === "if"
          ? `Step "${last.id}": no path does anything yet. Add a step that acts on the lead to a path.`
          : `Step "${last.id}": a workflow that only waits does nothing. Add a step after it that acts on the lead.`,
      stepId: last.id,
    };
  }
  return null;
}

/**
 * core-api's publish check of the starts ACL-408 adds (`workflow_body.publish_problem`), word for
 * word: "The stage changes" needs a stage to move to, and names only stages the tenant has; "A call
 * ends" needs which calls, refuses a call the lead made while those have no end event, and a least
 * talk time only goes with a call a rep talked on. Nothing is called unknown before the catalog
 * has loaded.
 */
function moreStartProblem(start: WorkflowStartsWhen, offered: CatalogStart | undefined): string | null {
  if (start.event === "lead.stage_changed") {
    const to = start.to_stages ?? [];
    if (to.length === 0) return "Choose at least one stage the lead moves to.";
    if (offered?.stages) {
      const known = new Set(offered.stages.map((s) => s.slug));
      const unknown = [...(start.from_stages ?? []), ...to].find((slug) => !known.has(slug));
      if (unknown) return `"${unknown}" is not one of your stages. Remove it to publish.`;
    }
  }
  if (start.event === "call.ended") {
    const directions = start.call_directions ?? ["outbound"];
    if (directions.length === 0) return "Choose which calls the workflow starts on.";
    const inbound = offered?.call_directions?.find((d) => d.value === "inbound");
    if (directions.includes("inbound") && inbound?.available === false) {
      return "A workflow can't start when a call the lead made ends yet: those calls don't say when they end. Choose calls we make.";
    }
    if ((start.min_talk_seconds ?? 0) > 0 && (start.call_answered ?? "any") !== "yes") {
      return "A least talk time needs a call a rep talked on. Choose that, or take the time off.";
    }
  }
  return null;
}

/** Why a Slack message's words can't be filled, as core-api's `slack_text_problem` says it. */
export function slackTextProblem(text: string, catalog: WorkflowCatalog | undefined): string | null {
  const fields = catalog?.merge_fields?.length ? catalog.merge_fields : MERGE_FIELDS;
  if (hasBrokenField(text)) return "a field is written between {{ and }}, like {{first_name}}.";
  const known = new Set(fields.map((f) => f.key));
  const unknown = fieldsIn(text).find((f) => !known.has(f.key));
  if (unknown) {
    return `{{${unknown.key}}} is not a detail the platform fills. Use ${[...known].map((k) => `{{${k}}}`).join(", ")}.`;
  }
  return null;
}

/** A person's name as the catalog lists them; null when they are not one of the tenant's active
 *  people (or the catalog has not loaded). */
export function personName(id: number | null, catalog: WorkflowCatalog | undefined): string | null {
  if (id === null) return null;
  return catalog?.people?.find((p) => p.id === id)?.name ?? null;
}

/**
 * core-api's publish check of the actions ACL-408 adds (`_person_problem`, `_task_problem`,
 * `_slack_problem`), word for word. A person or a connection is not called unknown before the
 * catalog has loaded.
 */
export function moreActionProblem(
  step: AssignOwnerStep | CreateTaskStep | SendSlackStep,
  catalog: WorkflowCatalog | undefined,
): WorkflowProblem | null {
  const on = (message: string): WorkflowProblem => ({ message: `Step "${step.id}": ${message}`, stepId: step.id });
  const person = (id: number | null, missing: string): WorkflowProblem | null => {
    if (id === null) return on(missing);
    if (catalog?.people && !catalog.people.some((p) => p.id === id)) {
      return on("the person it names is no longer one of your active people. Choose another.");
    }
    return null;
  };
  if (step.type === "assign_owner") return person(step.user_id, "choose who owns the lead.");
  if (step.type === "create_task") {
    if (!step.title?.trim()) return on("give the task a title.");
    if (step.due_in_seconds === null) return on("choose when the task is due.");
    if (step.assignee === null) return on("choose who the task is for.");
    if (step.assignee === "user") return person(step.user_id, "choose who the task is for.");
    return null;
  }
  if (step.connection_id === null) return on("choose a Slack connection.");
  if (catalog?.slack_connections) {
    const connection = catalog.slack_connections.find((c) => c.id === step.connection_id);
    if (!connection) return on("its Slack connection is not one of yours. Choose another.");
    if (!connection.usable) {
      return on(`the Slack connection "${connection.name}" is turned off or needs attention. Fix it in Settings, or choose another.`);
    }
  }
  if (!step.channel_id) return on("choose a channel.");
  if (!step.text?.trim()) return on("write the message.");
  const why = slackTextProblem(step.text, catalog);
  return why ? on(why) : null;
}

/** Every stage of the tenant by slug, end stages too, as the If/Else's catalog lists them. */
function allStageLabels(catalog: WorkflowCatalog | undefined): Map<string, string> {
  const stages = catalog?.condition_fields?.find((f) => f.field === "stage");
  return new Map((stages?.values ?? []).map((v) => [v.value, v.label]));
}

/** A stage's name as an admin knows it; its slug while nothing names it. */
export function stageName(slug: string, catalog: WorkflowCatalog | undefined): string {
  return catalog?.stages?.find((s) => s.slug === slug)?.label ?? allStageLabels(catalog).get(slug) ?? slug;
}

/** A field's title as an admin knows it; its key while nothing names it. */
export function fieldTitle(key: string, catalog: WorkflowCatalog | undefined): string {
  return catalog?.fields?.find((f) => f.key === key)?.title ?? key;
}

/** Whether an Update field step has no value yet. */
export function noValue(value: FieldStepValue | null): boolean {
  return value === null || value === "" || (Array.isArray(value) && value.length === 0);
}

/**
 * core-api's publish check of an action (ACL-370, `workflow_body._stage_problem`, `_field_problem`),
 * word for word. A stage or a field is not called unknown before the catalog has loaded. The
 * catalog offers only working and scheduled stages, so a stage it lists among the If/Else's and not
 * here is an end stage. A value is put in by the field's own input, which takes only what fits, so
 * of the field's checks only an option the field no longer has is said here; core-api says the rest.
 */
export function actionProblem(step: ActionStep, catalog: WorkflowCatalog | undefined): WorkflowProblem | null {
  const on = (message: string): WorkflowProblem => ({ message: `Step "${step.id}": ${message}`, stepId: step.id });
  if (step.type === "note") return step.text?.trim() ? null : on("write the note.");
  if (step.type === "set_stage") {
    if (!step.stage) return on("choose a stage.");
    if (!catalog) return null;
    const offered = catalog.stages?.find((s) => s.slug === step.stage);
    const label = stageName(step.stage, catalog);
    if (!offered) {
      return allStageLabels(catalog).has(step.stage)
        ? on(`"${label}" is an end stage: an outcome puts a lead there. Choose another.`)
        : on(`"${label}" is not one of your stages. Choose another.`);
    }
    if (offered.group === "scheduled" && step.call_in_seconds === null) {
      return on(`choose when the lead is called, for "${label}".`);
    }
    if (offered.group !== "scheduled" && step.call_in_seconds !== null) {
      return on(`"${label}" takes no time to call: only a scheduled stage does.`);
    }
    return null;
  }
  if (!step.field) return on("choose a field.");
  if (!catalog) return null;
  const field = catalog.fields?.find((f) => f.key === step.field);
  if (!field) return on(`"${step.field}" is not a field a workflow can change. Choose another.`);
  if (noValue(step.value)) return on(`choose a value for "${field.title}".`);
  const keys = new Set((field.config.options ?? []).map((o) => o.key));
  if (field.type === "dropdown" && !keys.has(String(step.value))) {
    return on(`the value for "${field.title}" is not one of the options.`);
  }
  if (field.type === "multi_select" && Array.isArray(step.value) && step.value.some((k) => !keys.has(k))) {
    return on(`the value for "${field.title}" has a choice that is not one of the options.`);
  }
  return null;
}

/** core-api's template check at publish (ACL-385): a text sends a template the tenant has, and
 *  that is on. Nothing is called missing before the catalog has loaded. */
function templateProblem(stepId: string, slug: string, catalog: WorkflowCatalog | undefined): WorkflowProblem | null {
  if (!catalog) return null;
  const template = catalog.templates.find((t) => t.slug === slug);
  if (!template) {
    return { message: `Step "${stepId}": its template is not one of yours. Choose another.`, stepId };
  }
  // An email template is not one a text may send: core-api reads it as one that is not on.
  if (template.active === false || template.channel === "email") {
    return {
      message: `Step "${stepId}": the template "${template.name}" is turned off. Turn it on or choose another.`,
      stepId,
    };
  }
  return null;
}

/** An email step's template, as core-api's `_template_problem` asks it (ACL-378). */
function emailTemplateProblemOf(stepId: string, slug: string, catalog: WorkflowCatalog | undefined): WorkflowProblem | null {
  if (!catalog) return null;
  const template = catalog.templates.find((t) => t.slug === slug);
  if (!template) {
    return { message: `Step "${stepId}": its template is not one of yours. Choose another.`, stepId };
  }
  if (template.active === false || template.channel !== "email") {
    return {
      message: `Step "${stepId}": the template "${template.name}" is turned off, or is not an email template. Choose another, or turn it on.`,
      stepId,
    };
  }
  return null;
}

/** The values the catalog offers for a field; undefined before it has loaded, when nothing is
 *  called unknown. */
function offeredValues(catalog: WorkflowCatalog | undefined, field: ConditionField): Set<string> | undefined {
  const offered = catalog?.condition_fields?.find((f) => f.field === field);
  return offered ? new Set(offered.values.map((v) => v.value)) : undefined;
}

/** core-api's `_condition_problem`: an If/Else needs a condition, a condition a value, and a firm
 *  or a stage must be one the tenant has. */
function conditionProblem(step: IfStep, catalog: WorkflowCatalog | undefined): WorkflowProblem | null {
  const on = (message: string) => ({ message: `Step "${step.id}": ${message}`, stepId: step.id });
  if (step.branches.length === 0) return on("add at least one path.");
  const seen = new Set<string>();
  for (const name of [...step.branches.map((b) => b.name), step.none.name].map((n) => n.trim())) {
    if (seen.has(name.toLowerCase())) return on(`two paths are called "${name}". Each path needs its own name.`);
    seen.add(name.toLowerCase());
  }
  for (const branch of step.branches) {
    const name = branch.name.trim();
    const where = `in the path "${name}", `;
    if (branch.conditions.length === 0) return on(`the path "${name}" needs at least one condition.`);
    for (const row of branch.conditions) {
      if (row.values.length === 0) return on(`${where}choose at least one value for ${CONDITION_FIELDS[row.field]}.`);
      if (row.field !== "firm" && row.field !== "stage") continue;
      const known = offeredValues(catalog, row.field);
      const unknown = known ? row.values.find((v) => !known.has(v)) : undefined;
      if (unknown) {
        return on(`${where}"${unknown}" is not one of your ${row.field === "firm" ? "firms" : "stages"}. Remove it to publish.`);
      }
    }
  }
  return null;
}

/** A value as the admin knows it: a firm's or a stage's label, "Spanish", or what was typed. */
export function valueLabel(field: ConditionField, value: string, catalog: WorkflowCatalog | undefined): string {
  const offered = catalog?.condition_fields?.find((f) => f.field === field);
  return offered?.values.find((v) => v.value === value)?.label ?? value;
}

/** A condition in words: "Firm is J&M or LHP", "State is not CA", "Source starts with facebook". */
export function conditionText(row: Condition, catalog: WorkflowCatalog | undefined): string {
  const verb =
    row.field === "source" ? (row.op === "is" ? "starts with" : "does not start with") : row.op === "is" ? "is" : "is not";
  const values = row.values.map((v) => valueLabel(row.field, v, catalog));
  return `${CONDITION_FIELDS[row.field]} ${verb} ${values.length ? orList(values) : "…"}`;
}

/** What a path checks, in words; every condition must hold. */
export function pathText(branch: WorkflowBranch, catalog: WorkflowCatalog | undefined): string {
  return branch.conditions.length
    ? branch.conditions.map((c) => conditionText(c, catalog)).join(" and ")
    : "No condition yet";
}

/** An If/Else's paths by name, as its box says them: "Passenger, Driver or None". */
export function ifText(step: IfStep): string {
  return orList(pathsOf(step).map((p) => p.name.trim() || "A path"));
}

/** Whether every path of an If/Else has its conditions, each with a value. */
export function ifReady(step: IfStep): boolean {
  return (
    step.branches.length > 0 &&
    step.branches.every((b) => b.conditions.length > 0 && b.conditions.every((c) => c.values.length > 0))
  );
}

/** The step a server sentence is about: core-api names it first, `Step "follow-up": …`. */
export function problemStep(detail: string): string | null {
  return /^Step "([a-z0-9][a-z0-9-]*)":/.exec(detail)?.[1] ?? null;
}

/**
 * Which card a sentence belongs on: the step it names, the first card for what is wrong with how
 * the workflow starts, or the page for the rest ("Add at least one step.", a name taken).
 */
export function problemPlace(detail: string): { stepId: string } | "start" | "page" {
  const stepId = problemStep(detail);
  if (stepId) return { stepId };
  if (
    /^(Choose (when|where|at least one outcome|at least one stage|which calls)|A workflow that starts|A workflow can't start|A least talk time|"[^"]+" is no longer one of your outcomes|"[^"]+" is not one of your stages)/.test(
      detail,
    )
  ) {
    return "start";
  }
  return "page";
}

/**
 * What a refused save or publish says, and the step it is about: core-api's 422 names the step
 * (`{message, step_id}`); anything else is a sentence for the page.
 */
export function problemFrom(error: unknown, fallback: string): { message: string; stepId: string | null } {
  if (!(error instanceof Error)) return { message: fallback, stepId: null };
  const detail = (error as Error & { body?: { detail?: unknown } }).body?.detail;
  const stepId =
    detail && typeof detail === "object" && typeof (detail as { step_id?: unknown }).step_id === "string"
      ? (detail as { step_id: string }).step_id
      : null;
  return { message: error.message || fallback, stepId };
}

/** A sentence as its card shows it: the card already says which step, so the server's `Step "x": `
 *  is dropped and the rest starts with a capital. */
export function problemOnCard(detail: string): string {
  const rest = detail.replace(/^Step "[^"]*": /, "");
  return rest.charAt(0).toUpperCase() + rest.slice(1);
}

/** "No answer or Busy": the names an admin picked, as one phrase. */
export function orList(words: readonly string[]): string {
  if (words.length <= 1) return words.join("");
  return `${words.slice(0, -1).join(", ")} or ${words[words.length - 1]}`;
}

/** When it starts, as the builder's first card says it: "An outcome is set: No answer". */
export function startText(start: WorkflowStartsWhen, catalog: WorkflowCatalog | undefined): string {
  if (start.event === null) return "Nothing chosen yet";
  const offered = catalog?.starts.find((s) => s.event === start.event);
  if (start.event === "lead.stage_changed" || start.event === "message.received" || start.event === "call.ended") {
    return moreStartText(start, catalog);
  }
  if (start.event === "disposition.set") {
    const labels = new Map((offered?.dispositions ?? []).map((d) => [d.slug, d.label]));
    const names = start.dispositions.map((slug) => labels.get(slug) ?? slug);
    return names.length ? `An outcome is set: ${orList(names)}` : "An outcome is set";
  }
  const labels = new Map((offered?.origins ?? []).map((o) => [o.value, o.label]));
  const names = start.origins.map((o) => labels.get(o) ?? o);
  return names.length ? `A new lead arrives from ${orList(names)}` : "A new lead arrives";
}

/** A start stage's name: from the start's own list (end stages too), else as the actions name it. */
export function startStageName(slug: string, catalog: WorkflowCatalog | undefined): string {
  const offered = catalog?.starts.find((s) => s.event === "lead.stage_changed");
  return offered?.stages?.find((s) => s.slug === slug)?.label ?? stageName(slug, catalog);
}

/** A talk time in words: "2 minutes", "45 seconds". */
export function talkText(seconds: number): string {
  if (seconds % 60 === 0) return seconds === 60 ? "1 minute" : `${seconds / 60} minutes`;
  return `${seconds} seconds`;
}

/**
 * The starts ACL-408 adds, in words, as core-api's `start_label` says them: "The stage changes:
 * from New to Chase", "The lead replies to a text", "A call ends: a rep talked with the lead for at
 * least 2 minutes".
 */
export function moreStartText(start: WorkflowStartsWhen, catalog: WorkflowCatalog | undefined): string {
  if (start.event === "message.received") return "The lead replies to a text";
  if (start.event === "lead.stage_changed") {
    const from = (start.from_stages ?? []).map((s) => startStageName(s, catalog));
    const to = (start.to_stages ?? []).map((s) => startStageName(s, catalog));
    let label = "The stage changes";
    if (from.length) label += `: from ${orList(from)}`;
    if (to.length) label += `${from.length ? " " : ": "}to ${orList(to)}`;
    return label;
  }
  const answered = start.call_answered ?? "any";
  if (answered === "yes") {
    const least = start.min_talk_seconds ?? 0;
    return `A call ends: a rep talked with the lead${least ? ` for at least ${talkText(least)}` : ""}`;
  }
  if (answered === "no") return "A call ends: nobody talked with the lead";
  return "A call ends";
}

function templateName(slug: string | null, catalog: WorkflowCatalog | undefined): string {
  const name = slug ? catalog?.templates.find((t) => t.slug === slug)?.name : undefined;
  return name ? `the text "${name}"` : "a text";
}

function emailName(slug: string | null, catalog: WorkflowCatalog | undefined): string {
  const name = slug ? catalog?.templates.find((t) => t.slug === slug)?.name : undefined;
  return name ? `the email "${name}"` : "an email";
}

/**
 * What publishing promises, in one paragraph, for the confirmation: "From now on, when an outcome
 * No answer is set on a lead, they wait 1 hour, then get the text "Follow-up". Each lead goes
 * through this once."
 */
export function publishSummary(body: WorkflowBody, catalog: WorkflowCatalog | undefined): string {
  const start = body.starts_when;
  const offered = catalog?.starts.find((s) => s.event === start.event);
  let when: string;
  if (start.event === "disposition.set") {
    const labels = outcomeLabels(catalog);
    const names = start.dispositions.map((slug) => labels.get(slug) ?? slug);
    when = `when ${names.length === 1 ? "the outcome" : "any of the outcomes"} ${orList(names)} is set on a lead`;
  } else if (start.event === "lead.created") {
    when = "when a new lead arrives";
  } else if (start.event === "lead.stage_changed") {
    const to = (start.to_stages ?? []).map((slug) => startStageName(slug, catalog));
    const from = (start.from_stages ?? []).map((slug) => startStageName(slug, catalog));
    when = `when a lead moves${from.length ? ` from ${orList(from)}` : ""} to ${orList(to) || "a stage"}`;
  } else if (start.event === "message.received") {
    when = "when a lead replies to a text";
  } else if (start.event === "call.ended") {
    const answered = start.call_answered ?? "any";
    const least = start.min_talk_seconds ?? 0;
    when =
      answered === "yes"
        ? `when a call ends that a rep talked on${least ? ` for at least ${talkText(least)}` : ""}`
        : answered === "no"
          ? "when a call ends that nobody talked on"
          : "when a call with a lead ends";
  } else {
    when = "when it starts";
  }
  const then = body.steps.length ? stepsPhrase(body.steps, catalog) : "nothing happens";
  return `From now on, ${when}, ${then}. Each lead goes through this once.`;
}

/**
 * "they wait 1 minute, then get a text", and an If/Else as "if Firm is J&M, they …; if State is CA,
 * …; otherwise
 * nothing more happens". An If/Else inside a path is put in brackets, so its "otherwise" is not
 * read as the outer one's; one whose paths are both empty changes nothing and is said as such.
 */
function stepsPhrase(steps: readonly WorkflowStep[], catalog: WorkflowCatalog | undefined, inner = false): string {
  const parts = steps.map((step) => {
    if (step.type === "wait") return `wait ${waitText(step.seconds)}`;
    if (step.type === "send_text") return `get ${templateName(step.template, catalog)}`;
    if (step.type === "send_email") return `get ${emailName(step.template, catalog)}`;
    if (step.type === "set_stage") return step.stage ? `are moved to "${stageName(step.stage, catalog)}"` : "are moved to a stage";
    if (step.type === "set_field") return step.field ? `have "${fieldTitle(step.field, catalog)}" set` : "have a field set";
    if (step.type === "note") return "get a note on their record";
    if (step.type === "assign_owner") {
      const name = personName(step.user_id, catalog);
      return name ? `are given to ${name}` : "are given to somebody";
    }
    if (step.type === "create_task") {
      const who = step.assignee === "owner" ? "their owner" : (personName(step.user_id, catalog) ?? "somebody");
      return step.title?.trim() ? `get a task "${step.title.trim()}" for ${who}` : `get a task for ${who}`;
    }
    if (step.type === "send_slack") return `are posted about in Slack${step.channel_name ? ` (#${step.channel_name})` : ""}`;
    if (pathsOf(step).every((p) => p.steps.length === 0)) return NOTHING_MORE;
    const path = (path: readonly WorkflowStep[]) => (path.length ? stepsPhrase(path, catalog, true) : NOTHING_MORE);
    const phrase = [
      ...step.branches.map((b) => `if ${pathText(b, catalog)} (${b.name.trim() || "a path"}), ${path(b.steps)}`),
      `otherwise ${path(step.none.steps)}`,
    ].join("; ");
    return inner ? `(${phrase})` : phrase;
  });
  const phrase = parts.join(", then ");
  const first = parts[0] ?? "";
  return steps[0]?.type === "if" || first === NOTHING_MORE ? phrase : `they ${phrase}`;
}

const NOTHING_MORE = "nothing more happens";

/** The status badge's words and tone. */
export function statusBadge(w: Pick<WorkflowSummary, "status" | "has_unpublished_changes">): {
  label: string;
  tone: "success" | "warning" | "neutral" | "outline";
} {
  if (w.status === "archived") return { label: "Archived", tone: "outline" };
  if (w.status === "draft") return { label: "Draft", tone: "neutral" };
  return w.has_unpublished_changes
    ? { label: "Published, with unpublished changes", tone: "warning" }
    : { label: "Published", tone: "success" };
}

export const RUN_STATUS_TEXT: Record<WorkflowRunStatus, string> = {
  active: "In progress",
  completed: "Finished",
  exited: "Stopped",
  failed: "Not finished",
};

/** Why a lead left a workflow, in words. An unknown reason reads as itself, underscores spaced. */
const END_REASONS: Record<string, string> = {
  removed_by_admin: "Removed by an admin",
  replied: "The lead replied",
  talked: "A rep talked to them",
  unpublished: "The workflow was unpublished",
  exit_stage: "Moved to an end stage",
  deleted: "The contact was deleted",
  stale: "Too late to send",
  no_branch: "Its path had no steps",
  no_text: "The text came out empty for this lead",
  template_off: "The template was turned off",
  not_set_up: "Texting is switched off",
  not_a_phone: "Their number is not a phone",
  number_cannot_text: "Our number cannot send texts",
  too_long: "The text is too long",
  opted_out: "They texted STOP",
  no_answer: "The provider did not answer; not sent again",
  stage_gone: "The stage is no longer one of yours",
  end_stage: "The stage is an end stage",
  in_end_stage: "The lead is in an end stage",
  field_gone: "The field is no longer one a workflow can change",
  bad_value: "The value no longer fits the field",
  no_note: "The note was empty",
  owner_gone: "The person to give the lead to has left",
  no_owner: "Nobody owns the lead, so the task had nobody",
  assignee_gone: "The person the task is for has left",
  no_number: "No number to text from",
  no_consent: "No consent to text",
  no_timezone: "Their time zone is unknown",
  dnc: "Do not call",
  stopped: "They texted STOP",
  interrupted: "Interrupted; not sent again",
  texts_unavailable: "Texts are not turned on yet",
  lost_step: "Its next step is no longer in the workflow",
  unknown_step: "A step this version cannot run",
  unreadable_version: "The workflow's version could not be read",
  unknown_send: "The text's send gave no answer",
};

/** Why an email step did not send (ACL-378): the email's own words where a text's would mislead. */
const EMAIL_REASONS: Record<string, string> = {
  email_stopped: "They replied STOP to an automated email",
  emails_unavailable: "Emails are not turned on yet",
  not_set_up: "Sending email is switched off",
  no_text: "The email came out empty for this lead",
  too_long: "The email is too long",
  no_address: "They have no email address",
  invalid_address: "Their email address is not one we can send to",
  our_address: "Their email address is one of ours",
  not_allowed: "Their address is not on the allowed list",
  opted_out: "They texted STOP",
  no_answer: "SendGrid did not answer; not sent again",
  unknown_send: "The email's send gave no answer",
};

/** Why a step or a lead's run ended, in words. `kind` is the step it ended on, when known: an
 *  email step's reasons read as an email's. */
export function endReasonText(reason: string | null, kind?: string | null): string | null {
  if (!reason) return null;
  const email = kind === "send_email";
  if (email && EMAIL_REASONS[reason]) return EMAIL_REASONS[reason];
  // A provider's own code (`twilio:21610`, `sendgrid:http_403`): it refused, and said why in a code.
  if (/^[a-z]+:[A-Za-z0-9_]+$/.test(reason)) {
    return email || reason.startsWith("sendgrid:") ? `SendGrid refused the email (${reason})` : `The carrier refused the text (${reason})`;
  }
  return END_REASONS[reason] ?? reason.replace(/_/g, " ");
}

/** The kind of step a lead's run ended on: the one kept failed with the run's reason, when there
 *  is one. */
export function endedAt(run: Pick<WorkflowRun, "steps" | "end_reason">): string | null {
  if (!run.end_reason) return null;
  return [...run.steps].reverse().find((s) => s.status === "failed" && s.error === run.end_reason)?.type ?? null;
}

/** The paths an If/Else sent the lead down, in the order taken, each named as it was then: the
 *  Leads tab's "Went: Spanish speakers → None". A step kept before paths had names reads as "None"
 *  for the None path and is left out otherwise. */
export function pathsTaken(run: Pick<WorkflowRun, "steps">): string[] {
  return run.steps.flatMap((s) => {
    if (s.type !== "if" || !s.branch) return [];
    const name = s.branch_name?.trim() || (s.branch === NONE_PATH ? "None" : "");
    return name ? [name] : [];
  });
}

/** The name a new workflow is made with, as GoHighLevel does it: "New workflow", then "New
 *  workflow 2" and on, the first one no workflow has. The builder lets the admin change it. */
export function nextWorkflowName(workflows: readonly Pick<WorkflowSummary, "name">[]): string {
  const taken = new Set(workflows.map((w) => w.name.trim().toLowerCase()));
  if (!taken.has("new workflow")) return "New workflow";
  for (let n = 2; ; n++) {
    if (!taken.has(`new workflow ${n}`)) return `New workflow ${n}`;
  }
}

/** The folders the list can be narrowed to, in order, each once whatever its case: "Leads" and
 *  "leads" are one folder, named as it was first written. */
export function folderNames(workflows: readonly WorkflowSummary[]): string[] {
  const seen = new Map<string, string>();
  for (const w of workflows) {
    const folder = w.folder?.trim();
    if (folder && !seen.has(folder.toLowerCase())) seen.set(folder.toLowerCase(), folder);
  }
  return [...seen.values()].sort((a, b) => a.localeCompare(b));
}

/**
 * The folder the list is filtered by: the one picked, while the list still has it. Another tenant's
 * list, or a list whose last workflow in it moved, drops it, so no filter stays on with no chip to
 * clear it, and no new workflow is made in a folder the list does not show.
 */
export function pickedFolder(picked: string | null, folders: readonly string[]): string | null {
  return picked !== null && folders.some((f) => f.trim().toLowerCase() === picked.trim().toLowerCase())
    ? picked
    : null;
}

/** Whether a workflow is in a folder, whatever the case it was typed in. */
export function inFolder(w: Pick<WorkflowSummary, "folder">, folder: string): boolean {
  return (w.folder ?? "").trim().toLowerCase() === folder.trim().toLowerCase();
}

/** What a lead does next, for the Enrollment history: "Wait", "Send SMS", "If / Else". */
/** Why the lead's next text waits, when it was moved rather than sent (ACL-385): the Leads tab's
 *  "Waiting for their 8 am". Null when the next step is not a moved text. */
export function heldText(run: Pick<WorkflowRun, "status" | "next_step" | "steps">): string | null {
  if (run.status !== "active") return null;
  const step = run.steps.find((s) => s.step_id === run.next_step && s.status === "waiting");
  if (!step) return null;
  if (step.held === "daily_limit") {
    return step.type === "send_email" ? "Waiting: today's email limit was reached" : "Waiting: today's text limit was reached";
  }
  if (step.held === "quiet_hours") return "Waiting for 8 am on their clock";
  return "Waiting to be sent";
}

const STEP_TYPE_TEXT: Record<WorkflowStepType, string> = {
  wait: "Wait",
  send_text: "Send SMS",
  send_email: "Send email",
  if: "If / Else",
  set_stage: "Update stage",
  set_field: "Update field",
  note: "Add note",
  assign_owner: "Assign owner",
  create_task: "Create task",
  send_slack: "Send Slack message",
};

export function nextStepText(type: WorkflowStepType | null): string {
  return type ? STEP_TYPE_TEXT[type] : "Next step";
}

/** What a lead's actions did, in order (ACL-370): the Leads tab's "Did: Moved to No response →
 *  Set Preferred Channel → Added a note". Empty when no action has run for them. */
export function actionsDone(run: Pick<WorkflowRun, "steps">): string[] {
  const words = (s: WorkflowRunStep): string | null => {
    if (s.type === "send_slack") {
      const where = s.channel_name ? ` #${s.channel_name}` : "";
      if (s.status === "done") return `Posted in Slack${where}`;
      if (s.status === "failed") return `Slack${where} not posted (${slackErrorText(s.error)})`;
      return null;
    }
    if (s.status !== "done") return null;
    if (s.type === "set_stage") return `Moved to ${s.stage_name ?? "a stage"}`;
    if (s.type === "set_field") return `Set ${s.field_name ?? "a field"}`;
    if (s.type === "note") return "Added a note";
    if (s.type === "assign_owner") return `Gave to ${s.person_name ?? "somebody"}`;
    if (s.type === "create_task") return `Task "${s.task_title ?? "a task"}" for ${s.person_name ?? "somebody"}`;
    return null;
  };
  return [...run.steps]
    .sort((a, b) => a.at.localeCompare(b.at))
    .map(words)
    .filter((w): w is string => w !== null);
}

/** Why a Slack message was not posted (ACL-408), in words: the step's own reasons, then Slack's
 *  codes the way an admin can act on them, then the code itself. */
const SLACK_ERRORS: Record<string, string> = {
  slack_gone: "its connection is gone",
  slack_off: "its connection is turned off",
  slack_unavailable: "Slack messages are not turned on",
  slack_unreachable: "Slack could not be reached",
  no_text: "the message came out empty",
  interrupted: "interrupted; not posted again",
  channel_not_found: "the channel was not found",
  not_in_channel: "the bot is not in the channel",
  is_archived: "the channel is archived",
  invalid_auth: "Slack refused the token",
  token_revoked: "Slack refused the token",
  account_inactive: "Slack refused the token",
  missing_scope: "the Slack app is missing a permission",
};

export function slackErrorText(error: string | null | undefined): string {
  if (!error) return "no reason given";
  return SLACK_ERRORS[error] ?? error.replace(/_/g, " ");
}

/** A step's label on its card and in the Leads tab: "Wait 1 hour", "Send a text". */
export function stepLabel(step: WorkflowStep | undefined, catalog: WorkflowCatalog | undefined): string {
  if (!step) return "A step";
  if (step.type === "wait") return `Wait ${waitText(step.seconds)}`;
  if (step.type === "if") return `If / Else: ${ifText(step)}`;
  if (step.type === "set_stage") return step.stage ? `Update stage: ${stageName(step.stage, catalog)}` : "Update stage";
  if (step.type === "set_field") return step.field ? `Update field: ${fieldTitle(step.field, catalog)}` : "Update field";
  if (step.type === "note") return "Add note";
  if (step.type === "assign_owner") {
    const name = personName(step.user_id, catalog);
    return name ? `Assign owner: ${name}` : "Assign owner";
  }
  if (step.type === "create_task") return step.title?.trim() ? `Create task: ${step.title.trim()}` : "Create task";
  if (step.type === "send_slack") return step.channel_name ? `Slack: #${step.channel_name}` : "Send Slack message";
  const name = step.template ? catalog?.templates.find((t) => t.slug === step.template)?.name : null;
  if (step.type === "send_email") return name ? `Email "${name}"` : "Send email";
  return name ? `Send "${name}"` : "Send SMS";
}

// --- the board's counts (ACL-407) ---------------------------------------------------------------

/** Where a lead is, as a count on the board names it; the runs list filters by the same words. */
export type BoardAt = "waiting" | "passed" | "failed" | "older";

const BOARD_ATS: readonly BoardAt[] = ["waiting", "passed", "failed", "older"];

/**
 * The leads one count on the board stands for: on a box now (`waiting`), past it (`passed`, and
 * for an If/Else down one `path`), failed at it, or on a step of an older version that the
 * published one does not have (`older`, no step).
 */
export interface BoardPlace {
  at: BoardAt;
  step?: string;
  path?: string;
}

export interface WorkflowPathCount {
  /** The path's id; `none` for None. */
  path: string;
  went: number;
}

export interface WorkflowStepCount {
  step_id: string;
  type: string;
  waiting: number;
  passed: number;
  failed: number;
  /** An If/Else's paths in its order, None last; null for every other step. */
  paths: WorkflowPathCount[] | null;
}

/** `GET /workflows/{id}/step-counts`: where the leads are, box by box, on the version last
 *  published. */
export interface WorkflowStepCounts {
  version_n: number | null;
  started: number;
  steps: WorkflowStepCount[];
  older: number;
  counted_at: string;
}

/** What the board draws from the counts. */
export interface BoardCounts {
  started: number;
  /** By step id: only the boxes on this board that the published version has, of the same kind.
   *  A box the draft added has none, so it shows no count. */
  steps: Map<string, WorkflowStepCount>;
  /** Leads on a step of an older version. */
  older: number;
  /** Leads waiting on a box of the published version that this draft took out: not on the board,
   *  so they are said apart rather than lost from the totals. */
  offBoard: number;
  countedAt: string;
}

export function boardCounts(counts: WorkflowStepCounts, body: WorkflowBody): BoardCounts {
  const kinds = new Map(allSteps(body.steps).map((s) => [s.id, s.type as string]));
  const steps = new Map<string, WorkflowStepCount>();
  let offBoard = 0;
  for (const s of counts.steps) {
    if (kinds.get(s.step_id) === s.type) steps.set(s.step_id, s);
    else offBoard += s.waiting;
  }
  return { started: counts.started, steps, older: counts.older, offBoard, countedAt: counts.counted_at };
}

/** How a box's "went past it" count reads: a text was sent, an action done. */
export const PASSED_WORD: Record<WorkflowStepType, string> = {
  wait: "passed",
  if: "passed",
  send_text: "sent",
  send_email: "sent",
  set_stage: "done",
  set_field: "done",
  note: "done",
  assign_owner: "done",
  create_task: "done",
  send_slack: "sent",
};

/** The place a page's address names (`at`, `box`, `path`), or null for none or one that cannot
 *  be. `box` and not `step`: the builder's own `step` is the box it brings into view. */
export function placeFromParams(params: URLSearchParams): BoardPlace | null {
  const at = params.get("at") as BoardAt | null;
  if (!at || !BOARD_ATS.includes(at)) return null;
  if (at === "older") return { at };
  const step = params.get("box");
  if (!step) return null;
  const path = params.get("path");
  return path && at === "passed" ? { at, step, path } : { at, step };
}

/** The address with this place in it, or with none. Everything else in it is kept. */
export function withPlace(params: URLSearchParams, place: BoardPlace | null): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of ["at", "box", "path"]) next.delete(key);
  if (place) {
    next.set("at", place.at);
    if (place.step) next.set("box", place.step);
    if (place.path) next.set("path", place.path);
  }
  return next;
}

/** The query the runs list takes for a place. */
export function placeQuery(place: BoardPlace | null | undefined): { at?: BoardAt; step?: string; path?: string } {
  return place ? { at: place.at, step: place.step, path: place.path } : {};
}

/** A place in words, for the Enrollment history's filter: `Wait 1 day · waiting now`, `If / Else:
 *  by language · went down "Spanish speakers"`. The step is named from the published version, or
 *  the draft when the published one does not have it. */
export function placeText(
  place: BoardPlace,
  bodies: readonly (WorkflowBody | null | undefined)[],
  catalog: WorkflowCatalog | undefined,
): string {
  if (place.at === "older") return "On a step of an older version";
  const step = bodies.map((b) => (b && place.step ? findStep(b, place.step) : undefined)).find(Boolean);
  const label = step ? stepLabel(step, catalog) : "A step no longer in the workflow";
  if (place.at === "waiting") return `${label} · waiting now`;
  if (place.at === "failed") return `${label} · failed`;
  if (place.path !== undefined) {
    const path = step?.type === "if" ? pathsOf(step).find((p) => p.key === place.path) : undefined;
    const name = path?.name.trim() || (place.path === NONE_PATH ? "None" : "a path");
    return `${label} · went down "${name}"`;
  }
  return `${label} · ${step ? PASSED_WORD[step.type] : "passed"}`;
}


// --- a lead's trail (ACL-406) -----------------------------------------------------------------

/** One line of a lead's trail: when, which box (named from the version the lead is on), and what
 *  happened there. */
export interface TrailLine {
  key: string;
  /** When it happened; null on the "Now" line. */
  at: string | null;
  label: string;
  detail: string;
  tone: "neutral" | "success" | "danger" | "accent";
  /** A text that was sent: the line opens the lead's conversation. */
  text?: boolean;
  /** An email that was sent (ACL-378): the line opens the lead's email thread. */
  email?: boolean;
  /** On the "Now" line: when the lead moves on, unless a text waits for its hours or its limit. */
  movesAt?: string | null;
  /** The box, for "Show on board". */
  stepId?: string;
}

/** The body a run was made from: the version the lead is on. The live one comes with the
 *  workflow; an older one is read on its own (`useWorkflowVersion`). Null until it is known. */
export function versionBody(
  run: Pick<WorkflowRun, "version_n">,
  workflow: Pick<WorkflowDetail, "published_version_n" | "published_body">,
  older: WorkflowBody | undefined,
): WorkflowBody | null {
  if (run.version_n !== null && run.version_n === workflow.published_version_n) return workflow.published_body;
  return older ?? null;
}

function stepDone(step: WorkflowRunStep, box: WorkflowStep | undefined): Omit<TrailLine, "key" | "at" | "label"> {
  if (step.status === "failed") {
    const why = endReasonText(step.error, step.type) ?? "it could not run";
    const sends = step.type === "send_text" || step.type === "send_email";
    return { detail: sends ? `Not sent: ${why}` : `Failed: ${why}`, tone: "danger" };
  }
  switch (step.type) {
    case "wait":
      return { detail: box?.type === "wait" ? `Waited ${waitText(box.seconds)}` : "Waited", tone: "neutral" };
    case "if":
      return { detail: `Went down "${step.branch_name ?? "a path"}"`, tone: "neutral" };
    case "send_text":
    case "send_email":
      if (step.status === "sending") return { detail: "Sending", tone: "accent" };
      if (step.status === "waiting") return { detail: "Waiting to be sent", tone: "accent" };
      return step.type === "send_email"
        ? { detail: "Sent", tone: "success", text: true, email: true }
        : { detail: "Sent", tone: "success", text: true };
    case "set_stage":
      return { detail: `Moved to ${step.stage_name ?? "a stage"}`, tone: "success" };
    case "set_field":
      return { detail: `Set ${step.field_name ?? "a field"}`, tone: "success" };
    case "note":
      return { detail: "Added a note", tone: "success" };
    default:
      return { detail: "Done", tone: "neutral" };
  }
}

/**
 * A lead's trail, oldest first: when the workflow started for them, one line per step they passed
 * with its time and what happened there, then the box they are on now and when they move on, or
 * why they finished. Boxes are named from `body`, the version the lead is on, so publishing again
 * never renames an old trail; a box that version does not name (or no body yet) is named by its
 * kind. The step a lead is waiting on is said once, on the "Now" line.
 */
export function trailLines(run: WorkflowRun, body: WorkflowBody | null, catalog: WorkflowCatalog | undefined): TrailLine[] {
  const boxes = body ? allSteps(body.steps) : [];
  const order = new Map(boxes.map((b, i) => [b.id, i]));
  const box = (id: string) => boxes.find((b) => b.id === id);
  const name = (id: string, type: string | null) => {
    const found = box(id);
    return found ? stepLabel(found, catalog) : nextStepText((type as WorkflowStepType) ?? null);
  };
  const lines: TrailLine[] = [
    {
      key: "start",
      at: run.started_at,
      label: "Started",
      detail: body ? startText(body.starts_when, catalog) : "The workflow started for this lead",
      tone: "neutral",
    },
  ];
  const waitingOn = run.status === "active" ? run.next_step : null;
  const kept = [...run.steps].sort(
    (a, b) => a.at.localeCompare(b.at) || (order.get(a.step_id) ?? 0) - (order.get(b.step_id) ?? 0),
  );
  for (const step of kept) {
    if (step.step_id === waitingOn && step.status !== "failed") continue;
    lines.push({ key: `step:${step.step_id}`, at: step.at, label: name(step.step_id, step.type), ...stepDone(step, box(step.step_id)) });
  }
  if (run.status === "active" && run.next_step) {
    const held = heldText(run);
    lines.push({
      key: "now",
      at: null,
      label: name(run.next_step, run.next_step_type),
      detail: held ?? (run.next_run_at ? "Moves on" : "Next"),
      tone: "accent",
      movesAt: held ? null : run.next_run_at,
      stepId: run.next_step,
    });
  } else if (run.status !== "active") {
    const why = endReasonText(run.end_reason, endedAt(run));
    lines.push({
      key: "end",
      at: run.ended_at,
      label: RUN_STATUS_TEXT[run.status],
      detail: why ?? (run.status === "completed" ? "Went through every step" : "Ended"),
      tone: run.status === "completed" ? "success" : run.status === "failed" ? "danger" : "neutral",
    });
  }
  return lines;
}

/** Whether "Show on board" can find the lead's box in the draft the builder shows; a box taken out
 *  of the draft since cannot be shown. */
export function onBoard(stepId: string | undefined, draft: WorkflowBody): boolean {
  return Boolean(stepId && findStep(draft, stepId));
}
